import { useEffect, useMemo, useState } from "react";
import { api, MediaJob, StreamVideo } from "../lib/api";
import { GENERATION_LANGS, languageName } from "../lib/languages";
import { estimateDubbingCost, formatEUR } from "../lib/costs";

const POLL_INTERVAL_MS = 5000;

/**
 * Top-level "auto-generate dubs + subtitles" UI on VideoDetail. One
 * generation produces both a translated subtitle track and a dubbed
 * audio track per target language; each output has its own
 * include-in-player toggle in the matrix below.
 */
export function GenerationPanel({
  video,
  jobs,
  onChanged,
}: {
  video: StreamVideo;
  jobs: MediaJob[];
  onChanged: () => void;
}) {
  const sourceLang = video.meta.source_lang;
  if (!sourceLang) return null;

  return (
    <section className="card p-6 space-y-5">
      <header>
        <h2 className="text-lg font-semibold">Auto-generate dubs &amp; subtitles</h2>
        <p className="text-sm text-neutral-500 mt-1">
          ElevenLabs Dubbing transcribes the {languageName(sourceLang)} audio, translates it,
          and produces both a dubbed audio track and a translated subtitle track per target language.
          Each output has its own toggle below.
        </p>
      </header>

      <GenerateForm video={video} jobs={jobs} onSubmitted={onChanged} />

      <JobMatrix video={video} jobs={jobs} onChanged={onChanged} />
    </section>
  );
}

function GenerateForm({
  video,
  jobs,
  onSubmitted,
}: {
  video: StreamVideo;
  jobs: MediaJob[];
  onSubmitted: () => void;
}) {
  const sourceLang = video.meta.source_lang!;
  // A target lang is "taken" if it has either a subtitle or dub row
  // that hasn't permanently failed. The user must remove it before
  // re-submitting.
  const takenLangs = useMemo(
    () => new Set(jobs.filter((j) => j.status !== "failed").map((j) => j.target_lang)),
    [jobs],
  );
  const eligible = GENERATION_LANGS.filter(
    (l) => !takenLangs.has(l) && l !== sourceLang,
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cost = estimateDubbingCost({
    durationSeconds: video.duration ?? 0,
    targetLangs: Array.from(selected),
  });

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function submit() {
    if (selected.size === 0 || !consent) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.generate(video.uid, {
        source_lang: sourceLang,
        target_langs: Array.from(selected),
        consent: true,
      });
      if (res.conflicts.length > 0) {
        setError(
          `Already exists for: ${res.conflicts.join(", ")}. Remove the existing row first to regenerate.`,
        );
      }
      setSelected(new Set());
      setConsent(false);
      onSubmitted();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (eligible.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        All supported target languages already have a generation job. Use the matrix below to
        regenerate or remove.
      </p>
    );
  }

  return (
    <div className="space-y-3 border border-neutral-200 rounded-md p-4">
      <div className="text-sm font-medium">Target languages</div>
      <div className="flex flex-wrap gap-2">
        {eligible.map((code) => {
          const on = selected.has(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              disabled={submitting}
              className={
                "text-xs px-3 py-1.5 rounded-full border transition-colors " +
                (on
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500")
              }
            >
              {languageName(code)}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-neutral-600">
        {selected.size === 0 ? (
          "Pick one or more languages."
        ) : (
          <>
            Estimated cost: <span className="font-medium">{formatEUR(cost.total)}</span>
            <span className="text-neutral-400"> · {formatEUR(cost.perLanguage)}/language</span>
          </>
        )}
      </div>

      <label className="flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={submitting}
          className="mt-0.5"
        />
        <span>
          I confirm the speaker has consented to AI voice cloning for the selected languages.
          Required for dub generation (GDPR).
        </span>
      </label>

      <button
        type="button"
        className="btn-primary"
        disabled={submitting || selected.size === 0 || !consent}
        onClick={submit}
      >
        {submitting ? "Submitting…" : `Generate (${selected.size})`}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function JobMatrix({
  video,
  jobs,
  onChanged,
}: {
  video: StreamVideo;
  jobs: MediaJob[];
  onChanged: () => void;
}) {
  useEffect(() => {
    const active = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (!active) return;
    const t = setInterval(() => onChanged(), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [jobs, onChanged]);

  // Group jobs by target_lang so each row shows both subtitle and dub.
  const byLang = useMemo(() => {
    const map = new Map<string, { subtitle?: MediaJob; dub?: MediaJob }>();
    for (const j of jobs) {
      const slot = map.get(j.target_lang) ?? {};
      slot[j.type] = j;
      map.set(j.target_lang, slot);
    }
    return map;
  }, [jobs]);

  if (byLang.size === 0) {
    return <p className="text-sm text-neutral-500">No generation jobs yet.</p>;
  }

  return (
    <div className="space-y-3">
      {Array.from(byLang.entries()).map(([lang, pair]) => (
        <LangRow key={lang} video={video} lang={lang} pair={pair} onChanged={onChanged} />
      ))}
    </div>
  );
}

function LangRow({
  video,
  lang,
  pair,
  onChanged,
}: {
  video: StreamVideo;
  lang: string;
  pair: { subtitle?: MediaJob; dub?: MediaJob };
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const primary = pair.dub ?? pair.subtitle!;
  const isSourceLang = primary.source_lang === lang;
  // Source-language rows are subtitle-only (you don't dub yourself).
  // Phrase the confirm prompts accordingly.
  const noun = isSourceLang ? "subtitle" : "dub + subtitle";

  async function regenerate() {
    if (!confirm(`Delete the existing ${languageName(lang)} ${noun} and regenerate?`)) return;
    setBusy(true);
    setError(null);
    try {
      if (pair.subtitle) await api.deleteJob(video.uid, pair.subtitle.id);
      if (pair.dub) await api.deleteJob(video.uid, pair.dub.id);
      await api.generate(video.uid, {
        source_lang: primary.source_lang,
        target_langs: [lang],
        consent: true,
      });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeAll() {
    if (!confirm(`Remove the ${languageName(lang)} ${noun} entirely?`)) return;
    setBusy(true);
    setError(null);
    try {
      if (pair.subtitle) await api.deleteJob(video.uid, pair.subtitle.id);
      if (pair.dub) await api.deleteJob(video.uid, pair.dub.id);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-neutral-200 rounded-md p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">
            {languageName(lang)}
            {isSourceLang && (
              <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-700 align-middle">
                source
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500">{lang}</div>
        </div>
        <div className="space-x-3 text-xs">
          <button
            className="text-neutral-700 hover:underline"
            onClick={regenerate}
            disabled={busy}
          >
            Regenerate
          </button>
          <button
            className="text-red-600 hover:underline"
            onClick={removeAll}
            disabled={busy}
          >
            Remove
          </button>
        </div>
      </div>

      <div className={isSourceLang ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-3"}>
        <OutputCell label="Subtitle" video={video} job={pair.subtitle} onChanged={onChanged} />
        {!isSourceLang && (
          <OutputCell label="Dub" video={video} job={pair.dub} onChanged={onChanged} />
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function OutputCell({
  label,
  video,
  job,
  onChanged,
}: {
  label: string;
  video: StreamVideo;
  job?: MediaJob;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      await api.setJobIncluded(video.uid, job.id, !(job.included_in_player === 1));
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!job) {
    return (
      <div className="p-2 rounded bg-neutral-50 text-xs text-neutral-500">
        <span className="font-medium text-neutral-700">{label}:</span> not generated
      </div>
    );
  }

  const included = job.included_in_player === 1;
  const styles: Record<string, string> = {
    queued: "bg-neutral-100 text-neutral-700",
    processing: "bg-blue-100 text-blue-800",
    ready: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
  };

  return (
    <div className="p-2 rounded bg-neutral-50 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-neutral-700">{label}</span>
        <span
          className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${styles[job.status]}`}
        >
          {job.status}
        </span>
      </div>
      {job.step && job.status !== "ready" && (
        <div className="text-xs text-neutral-500">{job.step.replace(/_/g, " ")}</div>
      )}
      {job.error && (
        <div className="text-xs text-red-600 break-words whitespace-pre-wrap">{job.error}</div>
      )}
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={included}
          disabled={job.status !== "ready" || busy}
          onChange={toggle}
        />
        <span className="text-xs text-neutral-600">{included ? "In player" : "Hidden"}</span>
      </label>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}
